import { useEffect, useState } from 'react';

import { useModules } from '../contexts/ModulesContext';
import {
    chunkInvoiceIds,
    distinctInvoiceIds,
    groupStatusRows,
    type InvoiceStatusRow,
} from '../domain/eInvoice';
import { moduleUnavailableReason } from '../domain/modules';
import useRequest from './useRequest';

interface StatusResponse {
    success: boolean;
    data?: InvoiceStatusRow[];
}

const NO_ROWS: ReadonlyMap<number, InvoiceStatusRow[]> = new Map();

/**
 * The e-Factura rows of the invoices a list shows (plan §7.3, Phase 3b).
 *
 * Asks nothing while the module is unavailable, so a core page that adds the column pays nothing
 * and depends on the module only through this hook. A failed read leaves the column empty: the
 * status is a convenience here, the E-Invoice page is the place that must be right.
 */
export function useEInvoiceStatuses(invoiceIds: readonly (number | null | undefined)[]) {
    const request = useRequest();
    const { modules, loaded } = useModules();
    const available = loaded && moduleUnavailableReason(modules, 'e-invoice') === null;
    const idKey = distinctInvoiceIds(invoiceIds).join(',');
    const [rowsByInvoice, setRowsByInvoice] = useState(NO_ROWS);

    useEffect(() => {
        if (!available || idKey === '') {
            setRowsByInvoice(NO_ROWS);
            return undefined;
        }

        let cancelled = false;
        const ids = idKey.split(',').map(Number);

        const load = async () => {
            try {
                const answers = await Promise.all(
                    chunkInvoiceIds(ids).map(chunk =>
                        request<StatusResponse>({
                            path: '/e-invoice/invoices/status',
                            params: { ids: chunk.join(',') },
                        }),
                    ),
                );

                if (!cancelled) {
                    setRowsByInvoice(groupStatusRows(answers.flatMap(answer => answer.data ?? [])));
                }
            } catch (error) {
                console.error('Loading e-invoice statuses failed:', error);

                if (!cancelled) {
                    setRowsByInvoice(NO_ROWS);
                }
            }
        };

        void load();

        return () => {
            cancelled = true;
        };
    }, [available, idKey, request]);

    return { available, rowsByInvoice };
}
